import { useMemo, useState, type FormEvent } from "react";
import { FileCheck2, Plus, RefreshCw, ShieldCheck, Users } from "lucide-react";

import {
  approveControlPolicyVersion,
  configureSupplierControl,
  controlPassportWorkspace,
  createInternalControlPolicyVersion,
  evaluateControlPassport,
  listTeam,
  listVendors,
} from "@/lib/api";
import { useData, matches } from "@/lib/app-view";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Empty, ErrorState, Loading } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { displayDate, titleCase } from "@/lib/utils";
import type { InternalControlEnforcement } from "@/lib/types";

type Props = {
  organisationId: string;
  refresh: number;
  query: string;
  canManage: boolean;
  canProcure: boolean;
  canApprove: boolean;
  changed: () => void;
};

export function ControlPassports({
  organisationId,
  refresh,
  query,
  canManage,
  canProcure,
  canApprove,
  changed,
}: Props) {
  const { data, error, loading } = useData(
    () =>
      Promise.all([
        controlPassportWorkspace(organisationId),
        listVendors(organisationId),
        listTeam(organisationId),
      ]).then(([workspace, vendors, team]) => ({ workspace, vendors, team })),
    [organisationId, refresh],
  );
  const [busy, setBusy] = useState("");
  const [actionError, setActionError] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);

  const cases = useMemo(
    () =>
      (data?.workspace.cases || []).filter((row) =>
        matches(
          query,
          row.control_id,
          row.case_reference,
          row.request?.request_no,
          row.request?.title,
          row.passport_state,
        ),
      ),
    [data, query],
  );

  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;

  async function evaluate(caseId: string) {
    setBusy(caseId);
    setActionError("");
    try {
      await evaluateControlPassport(organisationId, caseId);
      changed();
    } catch (reason) {
      setActionError((reason as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="PROCUREMENT CONTROL EVIDENCE"
        title="Procurement Control Passports"
        description="Explain why a purchase was allowed, why its supplier was selected, what was received and why payment became ready."
      >
        {canManage && (
          <Button className="min-h-11" onClick={() => setPolicyOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New control policy
          </Button>
        )}
      </PageHeader>

      <p className="rounded-xl border border-[var(--border)] bg-[var(--surface-secondary)] p-4 text-sm text-[var(--text-secondary)]">
        Evidence completeness is deterministic and policy-versioned. It is not a
        compliance score, certification, audit opinion or legal conclusion.
      </p>
      {actionError && <ErrorState message={actionError} />}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Control Passport summary">
        <Metric icon={FileCheck2} label="Controlled purchases" value={data.workspace.cases.length} />
        <Metric
          icon={ShieldCheck}
          label="Complete passports"
          value={data.workspace.cases.filter((row) => String(row.passport_state).startsWith("complete")).length}
        />
        <Metric icon={Users} label="Approved suppliers" value={data.workspace.approved_suppliers.length} />
        <Metric icon={RefreshCw} label="Policy versions" value={data.workspace.policies.reduce((sum, row) => sum + row.versions.length, 0)} />
      </section>

      <section className="grid gap-4" aria-labelledby="passport-heading">
        <div>
          <p className="eyebrow">PERMANENT CONTROL ID</p>
          <h2 id="passport-heading" className="mt-1 text-xl font-semibold">Purchase passports</h2>
        </div>
        {cases.length ? cases.map((row) => {
          const snapshot = row.snapshots?.[0];
          const latestEvidence = latestRequirements(row.evidence || []);
          const missing = latestEvidence.filter((item) => item.requirement_state === "required");
          return (
            <article className="surface p-5" key={row.id}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="eyebrow">{row.control_id}</p>
                  <h3 className="mt-1 text-lg font-semibold">{row.request?.title || row.case_reference}</h3>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    {row.request?.request_no} · {titleCase(row.control_origin)} · created {displayDate(row.created_at)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge value={row.passport_state} />
                  <Button
                    className="min-h-11"
                    variant="outline"
                    disabled={busy === row.id}
                    onClick={() => void evaluate(row.id)}
                  >
                    <RefreshCw className="size-4" aria-hidden="true" />
                    {busy === row.id ? "Evaluating…" : "Evaluate evidence"}
                  </Button>
                </div>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-4">
                <PassportDatum label="Completeness" value={snapshot ? `${snapshot.completeness_percent}%` : "Not evaluated"} />
                <PassportDatum label="Present" value={snapshot?.present_count ?? "—"} />
                <PassportDatum label="Missing" value={snapshot?.missing_count ?? "—"} />
                <PassportDatum label="Open exceptions" value={snapshot?.open_exception_count ?? row.exceptions?.length ?? 0} />
              </div>
              {missing.length > 0 && (
                <div className="mt-4 rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] p-4">
                  <strong className="text-sm text-[var(--warning)]">Missing policy evidence</strong>
                  <ul className="mt-2 grid gap-1 text-sm text-[var(--text-secondary)] sm:grid-cols-2">
                    {missing.map((item) => <li key={item.id}>• {item.label}</li>)}
                  </ul>
                </div>
              )}
              {snapshot && (
                <details className="mt-4 text-sm">
                  <summary className="cursor-pointer font-semibold">Governed snapshot details</summary>
                  <p className="mt-2 break-all text-[var(--text-secondary)]">
                    Version {snapshot.version} · SHA-256 {snapshot.sha256}
                  </p>
                </details>
              )}
            </article>
          );
        }) : (
          <Empty title="No matching Control Passport" description="Controlled purchases will retain a permanent Control ID and appear here." />
        )}
      </section>

      <section className="surface p-5" aria-labelledby="supplier-control-heading">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="eyebrow">APPROVED SUPPLIER LIST</p>
            <h2 id="supplier-control-heading" className="mt-1 text-xl font-semibold">Supplier qualification controls</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Qualification is buyer-specific and does not declare a supplier universally safe or legally compliant.
            </p>
          </div>
          {canProcure && (
            <Button className="min-h-11" variant="outline" onClick={() => setSupplierId(data.vendors[0]?.id || "")}>
              Configure supplier
            </Button>
          )}
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {data.workspace.supplier_controls.map((row) => (
            <article className="rounded-xl border border-[var(--border)] p-4" key={row.profile.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong>{row.vendor.name}</strong>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    {row.profile.category} · {titleCase(row.profile.criticality)}
                  </p>
                </div>
                <Badge value={row.profile.qualification_status} />
              </div>
              <p className="mt-3 text-sm">{row.profile.current_rationale}</p>
              <p className="mt-3 text-xs text-[var(--text-muted)]">
                Risk: {row.profile.risk_classification} · Evidence records: {row.documents.length}
              </p>
            </article>
          ))}
          {!data.workspace.supplier_controls.length && (
            <p className="text-sm text-[var(--text-secondary)]">No supplier has been risk-classified or qualified yet.</p>
          )}
        </div>
      </section>

      <section className="surface p-5" aria-labelledby="policy-heading">
        <p className="eyebrow">EFFECTIVE-DATED GOVERNANCE</p>
        <h2 id="policy-heading" className="mt-1 text-xl font-semibold">Purchase control policies</h2>
        <div className="mt-5 grid gap-3">
          {data.workspace.policies.map((row) => (
            <article className="rounded-xl border border-[var(--border)] p-4" key={row.policy.id}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <strong>{row.policy.name}</strong>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    {row.versions.length} immutable version{row.versions.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {row.versions[0] && <Badge value={row.versions[0].lifecycle_state} />}
                  {canApprove && row.versions[0]?.lifecycle_state === "draft" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        setActionError("");
                        try {
                          await approveControlPolicyVersion(
                            organisationId,
                            row.versions[0].id,
                            new Date().toISOString(),
                            "Approved for future controlled purchases",
                          );
                          changed();
                        } catch (reason) {
                          setActionError((reason as Error).message);
                        }
                      }}
                    >
                      Approve and activate
                    </Button>
                  )}
                </div>
              </div>
            </article>
          ))}
          {!data.workspace.policies.length && (
            <p className="text-sm text-[var(--text-secondary)]">No procurement control policy has been activated. Passport evaluation uses the transparent baseline evidence set.</p>
          )}
        </div>
      </section>

      <SupplierControlDialog
        open={Boolean(supplierId)}
        onOpenChange={(open) => !open && setSupplierId("")}
        organisationId={organisationId}
        vendors={data.vendors}
        team={data.team}
        initialVendorId={supplierId}
        onDone={changed}
      />
      <PolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        organisationId={organisationId}
        onDone={changed}
      />
    </>
  );
}

function latestRequirements(rows: Array<Record<string, any>>) {
  const latest = new Map<string, Record<string, any>>();
  for (const row of rows) {
    const current = latest.get(row.requirement_code);
    if (!current || Date.parse(row.evaluated_at) > Date.parse(current.evaluated_at)) {
      latest.set(row.requirement_code, row);
    }
  }
  return [...latest.values()];
}

function Metric({ icon: Icon, label, value }: { icon: typeof FileCheck2; label: string; value: number }) {
  return (
    <article className="surface p-4">
      <Icon className="size-5 text-[var(--action)]" aria-hidden="true" />
      <p className="mt-3 text-2xl font-semibold tabular-nums">{value}</p>
      <p className="mt-1 text-sm text-[var(--text-secondary)]">{label}</p>
    </article>
  );
}

function PassportDatum({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl bg-[var(--surface-secondary)] p-3">
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      <p className="mt-1 font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function SupplierControlDialog({
  open,
  onOpenChange,
  organisationId,
  vendors,
  team,
  initialVendorId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  vendors: Array<Record<string, any>>;
  team: Array<Record<string, any>>;
  initialVendorId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await configureSupplierControl(organisationId, {
        vendorId: String(form.get("vendor_id")),
        supplierType: String(form.get("supplier_type")),
        category: String(form.get("category")),
        criticality: String(form.get("criticality")),
        riskClassification: String(form.get("risk_classification")),
        qualificationStatus: String(form.get("qualification_status")),
        ownerId: String(form.get("owner_id") || ""),
        approverId: String(form.get("approver_id") || ""),
        validUntil: String(form.get("valid_until") || ""),
        nextReviewOn: String(form.get("next_review_on") || ""),
        rationale: String(form.get("rationale")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const approvers = team.filter((member) =>
    member.status === "active" && ["tenant_admin", "approver"].includes(member.role),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="SUPPLIER CONTROL" title="Classify and qualify supplier" description="The organisation owns the criteria and decision. Approved or conditional status requires a separate approver." />
        <form className="grid gap-4 p-6 sm:grid-cols-2" onSubmit={submit}>
          {error && <div className="sm:col-span-2"><ErrorState message={error} /></div>}
          <Label>Supplier<Select name="vendor_id" defaultValue={initialVendorId} required>{vendors.map((vendor) => <option value={vendor.id} key={vendor.id}>{vendor.name}</option>)}</Select></Label>
          <Label>Supplier type<Input name="supplier_type" defaultValue="organisation_supplier" required /></Label>
          <Label>Category<Input name="category" defaultValue="general" required /></Label>
          <Label>Criticality<Select name="criticality" defaultValue="routine"><option value="routine">Routine</option><option value="important">Important</option><option value="critical">Critical</option></Select></Label>
          <Label>Risk classification<Input name="risk_classification" defaultValue="not_classified" required /></Label>
          <Label>Qualification status<Select name="qualification_status" defaultValue="under_review">{["unqualified","under_review","approved","conditional","suspended","expired","rejected","offboarded"].map((value) => <option value={value} key={value}>{titleCase(value)}</option>)}</Select></Label>
          <Label>Qualification owner<Select name="owner_id"><option value="">Current user</option>{team.filter((member) => member.status === "active").map((member) => <option value={member.user_id} key={member.user_id}>{member.profiles?.full_name || member.profiles?.email}</option>)}</Select></Label>
          <Label>Qualification approver<Select name="approver_id"><option value="">Not assigned</option>{approvers.map((member) => <option value={member.user_id} key={member.user_id}>{member.profiles?.full_name || member.profiles?.email}</option>)}</Select></Label>
          <Label>Valid until<Input type="date" name="valid_until" /></Label>
          <Label>Next review<Input type="date" name="next_review_on" /></Label>
          <Label className="sm:col-span-2">Rationale<Textarea name="rationale" required minLength={10} placeholder="Record the evidence-based classification or qualification rationale." /></Label>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={busy} type="submit">{busy ? "Saving…" : "Record control decision"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const threshold = Number(form.get("quotation_threshold") || 0);
    const quoteCount = Number(form.get("quotation_count") || 0);
    setBusy(true);
    setError("");
    try {
      await createInternalControlPolicyVersion(organisationId, {
        name: String(form.get("name")),
        inheritanceMode: "merge",
        rules: [
          {
            rule_code: "QUOTATION-BASELINE",
            control_type: "quotation_count",
            priority: 100,
            amount_min: threshold,
            category: "*",
            required_quotation_count: quoteCount,
            enforcement: String(form.get("quotation_enforcement")) as InternalControlEnforcement,
          },
          {
            rule_code: "SUPPLIER-QUALIFICATION",
            control_type: "supplier_qualification",
            priority: 100,
            category: "*",
            enforcement: String(form.get("supplier_enforcement")) as InternalControlEnforcement,
          },
          {
            rule_code: "RECEIPT-BEFORE-PAYMENT",
            control_type: "receipt_control",
            priority: 100,
            category: "*",
            enforcement: String(form.get("receipt_enforcement")) as InternalControlEnforcement,
          },
        ],
        rationale: String(form.get("rationale")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="PURCHASE POLICY" title="Create a governed draft" description="Creating a version does not enforce it. A different authorised user must approve and activate it." />
        <form className="grid gap-4 p-6 sm:grid-cols-2" onSubmit={submit}>
          {error && <div className="sm:col-span-2"><ErrorState message={error} /></div>}
          <Label className="sm:col-span-2">Policy name<Input name="name" required minLength={3} /></Label>
          <Label>Quotation threshold<Input name="quotation_threshold" type="number" min="0" step="0.01" defaultValue="0" /></Label>
          <Label>Required quotations<Input name="quotation_count" type="number" min="0" max="20" defaultValue="3" /></Label>
          <Label>Quotation enforcement<Select name="quotation_enforcement" defaultValue="waiver_required"><option value="block">Hard block</option><option value="warn">Warning</option><option value="waiver_required">Approved waiver required</option><option value="observe">Observe only</option></Select></Label>
          <Label>Supplier qualification<Select name="supplier_enforcement" defaultValue="block"><option value="block">Hard block</option><option value="warn">Warning</option><option value="waiver_required">Approved waiver required</option></Select></Label>
          <Label className="sm:col-span-2">Receipt before payment-ready<Select name="receipt_enforcement" defaultValue="block"><option value="block">Hard block</option><option value="warn">Warning</option><option value="waiver_required">Approved waiver required</option></Select></Label>
          <Label className="sm:col-span-2">Change rationale<Textarea name="rationale" required minLength={10} /></Label>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={busy} type="submit">{busy ? "Creating…" : "Create draft version"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
